<template>
  <div class="sku-list-wrapper">
    <div class="search-bar">
      <div class="filters-row">
        <v-text-field
          v-model="search"
          append-icon="search"
          label="Design ID suchen"
          single-line
          hide-details
          dense
          outlined
          clearable
          class="filter-input"
        ></v-text-field>

        <v-select
          v-model="selectedCategories"
          :items="availableCategories"
          label="Kategorien filtern"
          multiple
          dense
          outlined
          hide-details
          clearable
          chips
          small-chips
          deletable-chips
          class="filter-input"
        >
          <template v-slot:selection="{ item, index }">
            <v-chip
              v-if="index < 1"
              x-small
              close
              color="secondary"
              dark
              @click:close="removeCategory(index)"
            >
              {{ item }}
            </v-chip>
            <span
              v-if="index === 1"
              class="grey--text text-caption"
            >
              (+{{ selectedCategories.length - 1 }})
            </span>
          </template>
        </v-select>

        <div v-if="hasActiveFilters" class="filter-info">
          <v-chip x-small color="primary" dark class="result-chip">
            {{ filteredDesignIds.length }} / {{ designIdGroups.length }}
          </v-chip>
        </div>

        <v-btn
          v-if="hasAnyFilters"
          x-small
          icon
          color="error"
          @click="clearAllFilters"
          class="clear-filters-btn"
        >
          <v-icon small>clear_all</v-icon>
        </v-btn>
      </div>
    </div>

    <div class="content-wrapper" ref="contentWrapper">
      <SimpleLoader v-if="loading" loading-text="Lade SKUs..." />

      <div v-else-if="designIdGroups.length === 0" class="empty-state">
        <v-icon size="64" color="grey lighten-1">inventory_2</v-icon>
        <p class="mt-4 text-h6 grey--text">Keine Design IDs vorhanden</p>
        <p class="text-caption grey--text">SKUs ohne Design ID werden ignoriert</p>
      </div>

      <v-expansion-panels v-else class="design-panels" multiple>
        <v-expansion-panel
          v-for="group in visibleDesignGroups"
          :key="group.designId"
          class="design-panel"
        >
          <v-expansion-panel-header class="panel-header">
            <div class="header-content">
              <div class="design-id-section">
                <!-- Design Image Thumbnail -->
                <div v-if="group.imageUrl" class="design-thumbnail">
                  <v-img
                    :src="group.imageUrl"
                    width="32"
                    height="32"
                    class="rounded"
                    contain
                  >
                    <template v-slot:placeholder>
                      <v-icon small color="grey lighten-2">mdi-image</v-icon>
                    </template>
                  </v-img>
                </div>
                <v-icon v-else small class="mr-2" color="primary">label</v-icon>
                <span class="design-id-text">Design ID: <strong>{{ group.designId }}</strong></span>
              </div>
              <div class="header-stats">
                <v-chip x-small color="info" dark class="mr-2">
                  {{ group.usedCategoriesCount }}/{{ categoriesThatSupportDesigns.length }} Kategorien
                </v-chip>
                <v-chip x-small color="success" dark>
                  {{ group.totalSkus }} SKUs
                </v-chip>
              </div>
            </div>
          </v-expansion-panel-header>

          <v-expansion-panel-content>
            <div class="categories-grid">
              <v-card
                v-for="category in categoriesThatSupportDesigns"
                :key="category.id"
                :class="getCategoryCardClass(group, category.id)"
                outlined
                class="category-card"
                @click="!isCategoryUsed(group, category.id) ? handleAddCategory(group, category) : null"
              >
                <v-card-text class="pa-2">
                  <div class="category-header">
                    <v-icon small :color="isCategoryUsed(group, category.id) ? 'success' : 'grey lighten-2'">
                      {{ isCategoryUsed(group, category.id) ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                    </v-icon>
                    <span class="category-name">{{ category.name }}</span>
                    <v-btn
                      v-if="!isCategoryUsed(group, category.id)"
                      x-small
                      icon
                      color="primary"
                      class="add-category-btn"
                      @click.stop="handleAddCategory(group, category)"
                    >
                      <v-icon x-small>add</v-icon>
                    </v-btn>
                  </div>

                  <div v-if="isCategoryUsed(group, category.id)" class="category-details">
                    <!-- SKU Count with Validation and TEEIN Button -->
                    <div class="sku-count-section">
                      <!-- Show validation for DXXX format Design IDs only -->
                      <v-chip
                        v-if="isDFormatDesignId(group.designId)"
                        x-small
                        :color="isSkuCountValid(group, category) ? 'success' : 'error'"
                        dark
                      >
                        {{ getSkuCountForCategory(group, category.id) }} / {{ getExpectedSkuCount(category) }} SKUs
                      </v-chip>
                      <!-- Show simple count for non-DXXX format Design IDs -->
                      <v-chip
                        v-else
                        x-small
                        color="info"
                        dark
                      >
                        {{ getSkuCountForCategory(group, category.id) }} SKUs
                      </v-chip>
                      <v-tooltip v-if="isDFormatDesignId(group.designId) && !isSkuCountValid(group, category)" top max-width="300">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon x-small color="error" v-bind="attrs" v-on="on" class="ml-1">
                            warning
                          </v-icon>
                        </template>
                        <span>
                          Erwartete SKUs: {{ getExpectedSkuCount(category) }} ({{ category.colors.length }} Farben × {{ category.sizes.length }} Größen)
                          <span v-if="hasTeeinSkus(group, category.id)">
                            <br>+ {{ getExpectedSkuCount(category) }} TEEIN Varianten
                          </span>
                        </span>
                      </v-tooltip>

                      <!-- TEEIN Button -->
                      <v-btn
                        v-if="hasTeeinSkus(group, category.id)"
                        x-small
                        color="info"
                        outlined
                        class="ml-2"
                        @click.stop="openTeeinDialog(group, category)"
                      >
                        <v-icon x-small left>mdi-tag-multiple</v-icon>
                        TEEIN ({{ getTeeinSkusForCategory(group, category.id).length }})
                      </v-btn>
                    </div>

                    <!-- Actual SKUs List -->
                    <div class="skus-list">
                      <div
                        v-for="skuItem in getDisplayedSkusForCategory(group, category.id)"
                        :key="skuItem.id"
                        class="sku-item"
                        @click="copySku(skuItem.sku)"
                      >
                        <div class="sku-item-wrapper">
                          <div class="sku-item-content">
                            <v-icon x-small color="grey darken-1">mdi-tag</v-icon>
                            <span class="sku-name">{{ skuItem.sku }}</span>
                          </div>
                          <div class="sku-status-indicators">
                          <!-- Poster Status Badge -->
                          <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                              <v-chip
                                x-small
                                :color="getPosterStatusColor(skuItem.posterStatus)"
                                :dark="skuItem.posterStatus === 'LIVE'"
                                class="status-chip"
                                v-bind="attrs"
                                v-on="on"
                              >
                                <v-icon x-small left>{{ getPosterStatusIcon(skuItem.posterStatus) }}</v-icon>
                                {{ getPosterStatusLabel(skuItem.posterStatus) }}
                              </v-chip>
                            </template>
                            <span>Poster Status: {{ skuItem.posterStatus || 'NOT_FOUND' }}</span>
                          </v-tooltip>

                          <!-- Translator Status Badge -->
                          <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                              <v-chip
                                x-small
                                :color="skuItem.translatorStatus ? 'success' : 'error'"
                                dark
                                class="status-chip"
                                v-bind="attrs"
                                v-on="on"
                              >
                                <v-icon x-small left>
                                  {{ skuItem.translatorStatus ? 'mdi-translate' : 'mdi-translate-off' }}
                                </v-icon>
                                {{ skuItem.translatorStatus ? 'T' : 'T' }}
                              </v-chip>
                            </template>
                            <span>Translator: {{ skuItem.translatorStatus ? 'Gefunden' : 'Nicht gefunden' }}</span>
                          </v-tooltip>

                          <!-- Candidate Status Badge -->
                          <v-tooltip top v-if="skuItem.candidate">
                            <template v-slot:activator="{ on, attrs }">
                              <v-chip
                                x-small
                                color="warning"
                                dark
                                class="status-chip"
                                v-bind="attrs"
                                v-on="on"
                              >
                                <v-icon x-small left>mdi-alert-circle</v-icon>
                                K
                              </v-chip>
                            </template>
                            <span>Kandidat - Noch nicht in Produktion</span>
                          </v-tooltip>
                        </div>
                        </div>
                      </div>

                      <!-- Expand/Collapse Button -->
                      <v-btn
                        v-if="hasMoreSkus(group, category.id)"
                        x-small
                        text
                        block
                        color="primary"
                        class="mt-2"
                        @click.stop="toggleCategoryExpansion(group, category.id)"
                      >
                        <v-icon x-small left>
                          {{ isCategoryExpanded(group, category.id) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                        </v-icon>
                        {{ isCategoryExpanded(group, category.id) ? 'Weniger anzeigen' : `${getSkusForCategory(group, category.id).length - 2} weitere anzeigen` }}
                      </v-btn>

                      <!-- Create Free SKU Button -->
                      <v-btn
                        x-small
                        text
                        block
                        color="orange"
                        class="mt-1"
                        @click.stop="openFreeSkuDialog(category)"
                      >
                        <v-icon x-small left>add_circle</v-icon>
                        Freie SKU erstellen
                      </v-btn>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- Unassigned SKUs Section -->
        <v-expansion-panel class="unassigned-panel" v-if="unassignedSkus.length > 0">
          <v-expansion-panel-header class="panel-header warning lighten-4">
            <div class="header-content">
              <div class="design-id-section">
                <v-icon small class="mr-2" color="warning">mdi-alert-circle</v-icon>
                <span class="design-id-text"><strong>Unzugewiesene SKUs</strong></span>
              </div>
              <div class="header-stats">
                <v-chip x-small color="warning" dark>
                  {{ unassignedSkus.length }} SKUs
                </v-chip>
              </div>
            </div>
          </v-expansion-panel-header>

          <v-expansion-panel-content>
            <v-simple-table dense class="unassigned-table">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Kategorie</th>
                    <th>Flags</th>
                    <th>Erstellt am</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="sku in unassignedSkus" :key="sku.id" @click="copySku(sku.name)" class="clickable-row">
                    <td class="sku-cell">
                      <v-icon x-small color="grey darken-1" class="mr-1">mdi-tag</v-icon>
                      <span class="sku-name">{{ sku.name }}</span>
                    </td>
                    <td>
                      <v-chip x-small color="secondary" dark>{{ sku.category || 'Unknown' }}</v-chip>
                    </td>
                    <td>
                      <v-chip
                        v-for="flag in sku.flags"
                        :key="flag"
                        x-small
                        color="info"
                        class="mr-1"
                      >
                        {{ flag }}
                      </v-chip>
                      <span v-if="!sku.flags || sku.flags.length === 0" class="text-caption grey--text">-</span>
                    </td>
                    <td class="text-caption">{{ formatDate(sku.createdAt) }}</td>
                    <td>
                      <v-chip
                        x-small
                        :color="sku.candidate ? 'warning' : 'success'"
                        dark
                      >
                        {{ sku.candidate ? 'KANDIDAT' : 'IN BENUTZUNG' }}
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>

    <!-- Snackbar für Copy-Feedback -->
    <v-snackbar
      v-model="snackbar"
      :timeout="2000"
      color="success"
      top
    >
      SKU wurde in die Zwischenablage kopiert!
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar = false"
        >
          Schließen
        </v-btn>
      </template>
    </v-snackbar>

    <!-- TEEIN SKUs Dialog -->
    <v-dialog v-model="teeinDialog" max-width="700" scrollable>
      <v-card v-if="teeinDialogData">
        <v-card-title class="info white--text">
          <v-icon left dark>mdi-tag-multiple</v-icon>
          TEEIN Varianten - {{ teeinDialogData.category.name }}
          <v-spacer></v-spacer>
          <v-chip small color="white" outlined class="mr-2">
            Design ID: {{ teeinDialogData.group.designId }}
          </v-chip>
          <v-chip small color="white" outlined class="mr-2">
            {{ teeinDialogData.skus.length }} SKUs
          </v-chip>
          <v-btn icon dark @click="closeTeeinDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4" style="max-height: 500px;">
          <div class="teein-skus-grid">
            <div
              v-for="skuItem in teeinDialogData.skus"
              :key="skuItem.id"
              class="teein-sku-item"
              @click="copySku(skuItem.sku)"
            >
              <div class="teein-sku-wrapper">
                <div class="teein-sku-content">
                  <v-icon x-small color="info">mdi-tag-multiple</v-icon>
                  <span class="teein-sku-name">{{ skuItem.sku }}</span>
                </div>
                <div class="teein-status-indicators">
                  <!-- Poster Status Badge -->
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        x-small
                        :color="getPosterStatusColor(skuItem.posterStatus)"
                        :dark="skuItem.posterStatus === 'LIVE'"
                        class="status-chip"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon x-small left>{{ getPosterStatusIcon(skuItem.posterStatus) }}</v-icon>
                        {{ getPosterStatusLabel(skuItem.posterStatus) }}
                      </v-chip>
                    </template>
                    <span>Poster Status: {{ skuItem.posterStatus || 'NOT_FOUND' }}</span>
                  </v-tooltip>

                  <!-- Translator Status Badge -->
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        x-small
                        :color="skuItem.translatorStatus ? 'success' : 'error'"
                        dark
                        class="status-chip"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon x-small left>
                          {{ skuItem.translatorStatus ? 'mdi-translate' : 'mdi-translate-off' }}
                        </v-icon>
                        T
                      </v-chip>
                    </template>
                    <span>Translator: {{ skuItem.translatorStatus ? 'Gefunden' : 'Nicht gefunden' }}</span>
                  </v-tooltip>

                  <!-- Candidate Status Badge -->
                  <v-tooltip top v-if="skuItem.candidate">
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        x-small
                        color="warning"
                        dark
                        class="status-chip"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon x-small left>mdi-alert-circle</v-icon>
                        K
                      </v-chip>
                    </template>
                    <span>Kandidat - Noch nicht in Produktion</span>
                  </v-tooltip>
                </div>
              </div>
            </div>
          </div>

          <v-alert type="info" dense class="mt-4 mb-0">
            <small>Klicken Sie auf eine SKU, um sie in die Zwischenablage zu kopieren.</small>
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeTeeinDialog">Schließen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Free SKU Creation Dialog -->
    <v-dialog v-model="freeSkuDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="orange white--text">
          <v-icon left dark>add_circle</v-icon>
          Freie SKU erstellen
        </v-card-title>

        <v-card-text class="pt-4">
          <v-alert type="info" dense class="mb-4">
            Erstellen Sie eine freie SKU zur Abdeckung von Typ-Problemen auf Marktplätzen.
          </v-alert>

          <v-text-field
            v-model="freeSku"
            label="SKU Name *"
            hint="z.B. custom_product_variant"
            persistent-hint
            outlined
            dense
            :rules="[v => !!v || 'SKU Name ist erforderlich']"
            class="mb-3"
          ></v-text-field>

          <v-text-field
            v-if="freeSkuCategoryName"
            :value="freeSkuCategoryName"
            label="Kategorie"
            outlined
            dense
            disabled
            class="mb-3"
          >
            <template v-slot:prepend-inner>
              <v-chip small color="secondary" dark>
                {{ freeSkuCategoryName }}
              </v-chip>
            </template>
          </v-text-field>

          <v-checkbox
            v-model="freeSkuIsTeein"
            label="TEEIN Variante"
            color="info"
            class="mt-0"
            hide-details
          ></v-checkbox>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeFreeSkuDialog"
            :disabled="isCreatingFreeSku"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="orange"
            @click="createFreeSku"
            :loading="isCreatingFreeSku"
            :disabled="!freeSku || !freeSkuCategory"
          >
            <v-icon left small>add</v-icon>
            SKU erstellen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import SimpleLoader from '@/components/Loading/SimpleLoader.vue';

export default {
  name: "SkuList",
  components: {
    SimpleLoader
  },
  props: {
    skus: {
      type: Array,
      default: () => [],
      // Now receives pre-grouped Design ID groups from backend (base SKUs only)
      // Format: [{ designId, skus[], categories[], totalSkus, usedCategoriesCount }]
    },
    teeinSkus: {
      type: Array,
      default: () => [],
      // TEEIN SKUs grouped by Design ID
      // Format: [{ designId, skus[], categories[], totalSkus, usedCategoriesCount }]
    },
    unassignedSkus: {
      type: Array,
      default: () => [],
      // SKUs without Design ID assignment
    },
    loading: {
      type: Boolean,
      default: false
    },
    allCategories: {
      type: Array,
      default: () => []
    }
  },
  data: () => ({
    search: "",
    snackbar: false,
    selectedCategories: [],
    teeinDialog: false,
    teeinDialogData: null, // { group, category, skus }
    expandedCategories: {}, // Track which categories are expanded: { 'designId-categoryId': true/false }

    // Free SKU Dialog
    freeSkuDialog: false,
    freeSku: '',
    freeSkuCategory: null,
    freeSkuCategoryName: '',
    freeSkuIsTeein: false,
    isCreatingFreeSku: false
  }),
  computed: {
    /**
     * Filter categories to only those that support designs
     */
    categoriesThatSupportDesigns() {
      return this.allCategories.filter(cat => cat.supportsDesigns !== false);
    },

    /**
     * Get available categories from the pre-grouped data
     */
    availableCategories() {
      const categoryNames = new Set();

      // Extract category names from all groups
      this.skus.forEach(group => {
        group.categories.forEach(cat => {
          categoryNames.add(cat.categoryName);
        });
      });

      return Array.from(categoryNames).filter(cat => cat).sort();
    },

    /**
     * Design ID groups - now just returns the pre-grouped data from backend
     * Sorted so DXXX format Design IDs are at the top (highest number first)
     */
    designIdGroups() {
      // The backend already provides the grouped data
      // Sort: DXXX format first (descending by number), then others alphabetically
      return [...this.skus].sort((a, b) => {
        const aIsDFormat = /^D\d+$/.test(a.designId);
        const bIsDFormat = /^D\d+$/.test(b.designId);

        // Both are D format - sort by number descending (D100 before D001)
        if (aIsDFormat && bIsDFormat) {
          const aNum = parseInt(a.designId.substring(1));
          const bNum = parseInt(b.designId.substring(1));
          return bNum - aNum; // Descending order
        }

        // Both are not D format - sort alphabetically
        if (!aIsDFormat && !bIsDFormat) {
          return a.designId.localeCompare(b.designId);
        }

        // D format comes first
        return aIsDFormat ? -1 : 1;
      });
    },

    /**
     * Filter design groups based on search and category filters
     */
    filteredDesignIds() {
      let filtered = this.designIdGroups;

      // Filter by search (Design ID)
      if (this.search) {
        const searchLower = this.search.toLowerCase();
        filtered = filtered.filter(group =>
          group.designId.toLowerCase().includes(searchLower)
        );
      }

      // Filter by categories (show only design IDs that are used in selected categories)
      if (this.selectedCategories.length > 0) {
        filtered = filtered.filter(group =>
          group.categories.some(cat =>
            this.selectedCategories.includes(cat.categoryName)
          )
        );
      }

      return filtered;
    },

    /**
     * Visible design groups (for future pagination if needed)
     */
    visibleDesignGroups() {
      return this.filteredDesignIds;
    },

    /**
     * Check if any filters are active
     */
    hasActiveFilters() {
      return this.search || this.selectedCategories.length > 0;
    },

    /**
     * Check if any filters exist
     */
    hasAnyFilters() {
      return this.selectedCategories.length > 0;
    }
  },
  methods: {
    /**
     * Check if a category is used by a design group
     */
    isCategoryUsed(group, categoryId) {
      return group.categories.some(cat => cat.categoryId === categoryId);
    },

    /**
     * Get SKU count for a specific category in a design group
     */
    getSkuCountForCategory(group, categoryId) {
      // Backend already excludes TEEIN SKUs, so just return the SKU count
      return this.getSkusForCategory(group, categoryId).length;
    },

    /**
     * Check if Design ID is in DXXX format
     */
    isDFormatDesignId(designId) {
      return /^D\d+$/.test(designId);
    },

    /**
     * Get expected SKU count based on colors × sizes
     */
    getExpectedSkuCount(category) {
      const colorsCount = category.colors?.length || 0;
      const sizesCount = category.sizes?.length || 0;
      return colorsCount * sizesCount;
    },

    /**
     * Validate if SKU count matches expected count
     * Allows for 2x the expected count when TEEIN SKUs are present
     * Skips validation for non-DXXX format Design IDs
     */
    isSkuCountValid(group, category) {
      // Skip validation for non-DXXX format Design IDs
      if (!this.isDFormatDesignId(group.designId)) {
        return true; // Always valid for custom Design IDs
      }

      const actualCount = this.getSkuCountForCategory(group, category.id);
      const teeinCount = this.getTeeinSkusForCategory(group, category.id).length;
      const expectedCount = this.getExpectedSkuCount(category);

      // If we have TEEIN SKUs, allow up to 2x the expected count (base + TEEIN)
      if (teeinCount > 0) {
        return actualCount === expectedCount && teeinCount === expectedCount;
      }

      return actualCount === expectedCount;
    },

    /**
     * Get actual SKUs for a specific category in a design group
     */
    getSkusForCategory(group, categoryId) {
      // Backend already excludes TEEIN SKUs from the main groups
      return group.skus.filter(sku => sku.categoryId === categoryId);
    },

    /**
     * Get TEEIN SKUs for a specific category in a design group
     */
    getTeeinSkusForCategory(group, categoryId) {
      // Find the matching TEEIN group by designId
      const teeinGroup = this.teeinSkus.find(g => g.designId === group.designId);
      if (!teeinGroup) return [];

      // Filter by category
      return teeinGroup.skus.filter(sku => sku.categoryId === categoryId);
    },

    /**
     * Check if a category has TEEIN SKUs
     */
    hasTeeinSkus(group, categoryId) {
      return this.getTeeinSkusForCategory(group, categoryId).length > 0;
    },

    /**
     * Open TEEIN SKUs dialog
     */
    openTeeinDialog(group, category) {
      const teeinSkus = this.getTeeinSkusForCategory(group, category.id);
      this.teeinDialogData = {
        group,
        category,
        skus: teeinSkus
      };
      this.teeinDialog = true;
    },

    /**
     * Close TEEIN SKUs dialog
     */
    closeTeeinDialog() {
      this.teeinDialog = false;
      this.teeinDialogData = null;
    },

    /**
     * Get CSS class for category card
     */
    getCategoryCardClass(group, categoryId) {
      if (!this.isCategoryUsed(group, categoryId)) {
        return 'unused-category';
      }

      // Check for problems with SKUs
      const hasProblems = this.categoryHasProblems(group, categoryId);

      return hasProblems ? 'used-category category-with-problems' : 'used-category';
    },

    /**
     * Check if a category has any problems (SKU count mismatch)
     * Skips ALL validation for non-DXXX format Design IDs
     * Only checks SKU count, not poster/translator status
     */
    categoryHasProblems(group, categoryId) {
      // Skip ALL validation for non-DXXX format Design IDs (like "BUNT", etc.)
      if (!this.isDFormatDesignId(group.designId)) {
        return false; // Never show red for custom Design IDs
      }

      // For DXXX format Design IDs: only check SKU count validation
      const category = this.allCategories.find(cat => cat.id === categoryId);
      if (category) {
        const countInvalid = !this.isSkuCountValid(group, category);
        return countInvalid;
      }

      return false;
    },

    /**
     * Get expansion key for a category
     */
    getExpansionKey(group, categoryId) {
      return `${group.designId}-${categoryId}`;
    },

    /**
     * Check if a category is expanded
     */
    isCategoryExpanded(group, categoryId) {
      const key = this.getExpansionKey(group, categoryId);
      return this.expandedCategories[key] === true;
    },

    /**
     * Toggle category expansion
     */
    toggleCategoryExpansion(group, categoryId) {
      const key = this.getExpansionKey(group, categoryId);
      this.$set(this.expandedCategories, key, !this.expandedCategories[key]);
    },

    /**
     * Get displayed SKUs for a category (limited to 2 unless expanded)
     */
    getDisplayedSkusForCategory(group, categoryId) {
      const allSkus = this.getSkusForCategory(group, categoryId);
      const isExpanded = this.isCategoryExpanded(group, categoryId);

      if (isExpanded || allSkus.length <= 2) {
        return allSkus;
      }

      return allSkus.slice(0, 2);
    },

    /**
     * Check if category has more than 2 SKUs (needs expand button)
     */
    hasMoreSkus(group, categoryId) {
      return this.getSkusForCategory(group, categoryId).length > 2;
    },

    /**
     * Remove category from filter
     */
    removeCategory(index) {
      this.selectedCategories.splice(index, 1);
    },

    /**
     * Clear all filters
     */
    clearAllFilters() {
      this.selectedCategories = [];
      this.search = '';
    },

    /**
     * Copy SKU to clipboard
     */
    copySku(sku) {
      navigator.clipboard.writeText(sku).then(() => {
        this.snackbar = true;
      }).catch(err => {
        console.error('Fehler beim Kopieren:', err);
      });
    },

    /**
     * Handle adding a category to an existing Design ID
     */
    handleAddCategory(group, category) {
      this.$emit('add-category-to-design', {
        designId: group.designId,
        categoryId: category.id,
        categoryName: category.name
      });
    },

    /**
     * Get poster status icon based on status
     */
    getPosterStatusIcon(status) {
      const statusMap = {
        'LIVE': 'mdi-image-check',
        'NOT_FOUND': 'mdi-image-off',
        'GENERATING': 'mdi-image-sync',
        'ERROR': 'mdi-image-remove'
      };
      return statusMap[status] || 'mdi-image-off';
    },

    /**
     * Get poster status color based on status
     */
    getPosterStatusColor(status) {
      const colorMap = {
        'LIVE': 'success',
        'NOT_FOUND': 'error',
        'GENERATING': 'warning',
        'ERROR': 'error'
      };
      return colorMap[status] || 'error';
    },

    /**
     * Get poster status label (short form)
     */
    getPosterStatusLabel(status) {
      const labelMap = {
        'LIVE': 'P',
        'NOT_FOUND': 'P',
        'GENERATING': 'P',
        'ERROR': 'P'
      };
      return labelMap[status] || 'P';
    },

    /**
     * Format date for display
     */
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    /**
     * Open free SKU dialog with pre-selected category
     */
    openFreeSkuDialog(category) {
      this.freeSkuCategory = category.id;
      this.freeSkuCategoryName = category.name;
      this.freeSkuDialog = true;
    },

    /**
     * Close free SKU dialog
     */
    closeFreeSkuDialog() {
      this.freeSkuDialog = false;
      this.freeSku = '';
      this.freeSkuCategory = null;
      this.freeSkuCategoryName = '';
      this.freeSkuIsTeein = false;
    },

    /**
     * Create a free SKU
     */
    async createFreeSku() {
      if (!this.freeSku || !this.freeSkuCategory) {
        this.$emit('show-message', 'Bitte füllen Sie alle Pflichtfelder aus', 'warning');
        return;
      }

      this.isCreatingFreeSku = true;

      try {
        // Build flags array based on TEEIN checkbox
        const flags = this.freeSkuIsTeein ? ['TEEIN'] : [];

        // Emit event to parent to handle the actual creation
        this.$emit('create-free-sku', {
          sku: this.freeSku,
          categoryId: this.freeSkuCategory,
          flags: flags
        });

        this.$emit('show-message', `Freie SKU "${this.freeSku}" wird erstellt...`, 'info');
        this.closeFreeSkuDialog();
      } catch (error) {
        console.error('Error creating free SKU:', error);
        this.$emit('show-message', 'Fehler beim Erstellen der freien SKU', 'error');
      } finally {
        this.isCreatingFreeSku = false;
      }
    }
  }
};
</script>

<style scoped>
/* SKU List Wrapper */
.sku-list-wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Search Bar */
.search-bar {
  flex-shrink: 0;
  padding: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%);
  border-bottom: 2px solid #e3f2fd;
}

.filters-row {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.filter-input {
  flex: 1 1 200px;
  min-width: 200px;
  max-width: 300px;
}

.filter-input >>> .v-input__control {
  border-radius: 8px !important;
}

.filter-input >>> .v-text-field__details {
  display: none !important;
}

.filter-input >>> fieldset {
  border-color: #e0e0e0 !important;
  transition: all 0.3s ease;
}

.filter-input >>> .v-input--is-focused fieldset {
  border-color: #1976d2 !important;
  border-width: 2px !important;
  box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
}

.filter-input >>> .v-select__selections {
  padding: 2px 0;
}

.filter-info {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.result-chip {
  font-weight: 600 !important;
  letter-spacing: 0.5px;
}

.clear-filters-btn {
  flex-shrink: 0;
}

/* Content Wrapper - Scrollable Area */
.content-wrapper {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px;
  scroll-behavior: smooth;
}

.content-wrapper::-webkit-scrollbar {
  width: 10px;
}

.content-wrapper::-webkit-scrollbar-track {
  background: transparent;
}

.content-wrapper::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #4caf50 0%, #388e3c 100%);
  border-radius: 10px;
}

.content-wrapper::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #388e3c 0%, #2e7d32 100%);
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

/* Design Panels */
.design-panels {
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  gap: 12px;
}

.design-panel {
  border-radius: 8px !important;
  border: 2px solid #e0e0e0 !important;
  transition: all 0.3s ease;
}

.design-panel:hover {
  border-color: #4caf50 !important;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.15) !important;
}

.design-panel >>> .v-expansion-panel-content__wrap {
  padding: 0 !important;
}

.design-panel >>> .v-expansion-panel-content {
  padding: 0 !important;
  margin: 0 !important;
}

.panel-header {
  background: linear-gradient(135deg, #f5f9fc 0%, #e3f2fd 100%);
  padding: 12px 20px !important;
  min-height: 60px !important;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.design-id-section {
  display: flex;
  align-items: center;
  gap: 8px;
}

.design-thumbnail {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border: 2px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.design-thumbnail:hover {
  border-color: #4caf50;
  transform: scale(1.1);
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
}

.design-id-text {
  font-size: 15px;
  color: #2c3e50;
}

.header-stats {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Categories Grid */
.categories-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  padding: 8px;
}

.categories-grid::-webkit-scrollbar {
  width: 6px;
}

.categories-grid::-webkit-scrollbar-track {
  background: #f5f5f5;
  border-radius: 3px;
}

.categories-grid::-webkit-scrollbar-thumb {
  background: #4caf50;
  border-radius: 3px;
}

.categories-grid::-webkit-scrollbar-thumb:hover {
  background: #388e3c;
}

.category-card {
  border-radius: 6px !important;
  transition: all 0.3s ease;
  cursor: default;
  display: flex;
  flex-direction: column;
}

.category-card.used-category {
  background: #f1f8e9;
  border: 2px solid #4caf50 !important;
}

.category-card.used-category:hover {
  background: #e8f5e9;
  box-shadow: 0 4px 8px rgba(76, 175, 80, 0.2) !important;
  transform: translateY(-2px);
}

/* Category with problems (missing posters, translator issues, etc.) */
.category-card.category-with-problems {
  background: #ffebee !important;
  border: 2px solid #f44336 !important;
}

.category-card.category-with-problems:hover {
  background: #ffcdd2 !important;
  box-shadow: 0 4px 8px rgba(244, 67, 54, 0.3) !important;
  transform: translateY(-2px);
}

.category-card.unused-category {
  background: #fafafa;
  border: 1px solid #e0e0e0 !important;
}

.category-card.unused-category:hover {
  background: #f5f5f5;
}

.category-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.category-name {
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
  line-height: 1.2;
  flex: 1;
}

.unused-category .category-name {
  color: #9e9e9e;
  font-weight: 500;
}

.add-category-btn {
  opacity: 0;
  transition: opacity 0.2s ease;
}

.unused-category:hover .add-category-btn {
  opacity: 1;
}

.unused-category {
  cursor: pointer;
}

.unused-category:hover .category-name {
  color: #1976d2;
}

.category-info {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
}

.category-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}

.sku-count-section {
  display: flex;
  align-items: center;
  gap: 4px;
}

.skus-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.skus-list::-webkit-scrollbar {
  width: 4px;
}

.skus-list::-webkit-scrollbar-track {
  background: #f5f5f5;
  border-radius: 2px;
}

.skus-list::-webkit-scrollbar-thumb {
  background: #4caf50;
  border-radius: 2px;
}

.sku-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.sku-item:hover {
  background: #e8f5e9;
  border-color: #4caf50;
  transform: translateX(2px);
}

.sku-item-wrapper {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.sku-item-content {
  display: flex;
  align-items: center;
  gap: 4px;
}

.sku-name {
  font-size: 11px;
  font-family: 'Courier New', monospace;
  color: #2c3e50;
  word-break: break-all;
  line-height: 1.3;
  flex: 1;
}

.sku-status-indicators {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.status-chip {
  font-weight: 600 !important;
  font-size: 10px !important;
  letter-spacing: 0.3px !important;
  height: 20px !important;
  border-radius: 10px !important;
}

.status-chip >>> .v-chip__content {
  padding: 0 8px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 3px !important;
  height: 100% !important;
}

.status-chip >>> .v-icon {
  font-size: 11px !important;
  margin: 0 !important;
}

/* TEEIN Dialog Styles */
.teein-skus-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;
}

.teein-sku-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.teein-sku-item:hover {
  background: #e3f2fd;
  border-color: #2196f3;
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.15);
}

.teein-sku-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.teein-sku-content {
  display: flex;
  align-items: center;
  gap: 6px;
}

.teein-sku-name {
  font-size: 12px;
  font-family: 'Courier New', monospace;
  color: #2c3e50;
  word-break: break-all;
  line-height: 1.4;
  flex: 1;
  font-weight: 500;
}

.teein-status-indicators {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

/* Responsive Grid */
@media (max-width: 1400px) {
  .categories-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 1024px) {
  .categories-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .categories-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    padding: 12px;
  }

  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .header-stats {
    width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 480px) {
  .categories-grid {
    grid-template-columns: 1fr;
  }
}

/* Unassigned SKUs Panel */
.unassigned-panel {
  margin-top: 16px;
}

.unassigned-table {
  background: white;
}

.unassigned-table thead th {
  font-weight: 600;
  color: #2c3e50;
  background: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  padding: 12px 16px;
}

.unassigned-table tbody td {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
}

.clickable-row {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.clickable-row:hover {
  background-color: #f8f9fa;
}

.sku-cell {
  display: flex;
  align-items: center;
}

.sku-cell .sku-name {
  font-family: monospace;
  font-size: 13px;
  font-weight: 500;
}
</style>

